import { useState, useEffect } from 'react';
import { Upload, AlertTriangle, Info, History, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { getGlobalSettings, toggleBulkUpload } from 'wasp/client/operations';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import ImportHistoryTable from '../bulk-upload/components/ImportHistoryTable';
import { UniversalImportDialog } from '../bulk-upload/components/UniversalImportDialog';

type BulkUploadSettings = {
  enabled: boolean;
  enabledBy?: string;
  enabledAt?: string;
};

const BulkUploadTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [universalDialogOpen, setUniversalDialogOpen] = useState(false);

  useEffect(() => {
    loadBulkUploadSettings();
  }, []);

  const loadBulkUploadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getGlobalSettings();
      const bulkUploadSetting = settings.find((s: any) => s.settingKey === 'bulk_upload_enabled');

      if (bulkUploadSetting) {
        const value = bulkUploadSetting.settingValue as BulkUploadSettings;
        setEnabled(value.enabled || false);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load bulk upload settings');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickToggle = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);

    try {
      setSaving(true);

      await toggleBulkUpload({
        enabled: newEnabled
      });

      toast.success(
        newEnabled
          ? 'Bulk upload enabled successfully'
          : 'Bulk upload disabled successfully'
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle bulk upload');
      // Revert on error
      setEnabled(!newEnabled);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">Loading bulk upload settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Tabs defaultValue="settings" className="w-full">
        <div className="px-6 pt-6">
          <TabsList>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Import History
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="settings">
          <div className="px-6 py-6 space-y-6">
            {/* Status Alert */}
            {enabled ? (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Bulk Upload is Enabled</AlertTitle>
                <AlertDescription>
                  Administrators can bulk upload data via CSV files across various admin pages.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Bulk Upload is Disabled</AlertTitle>
                <AlertDescription>
                  The bulk upload functionality is currently disabled. Enable it to allow CSV imports.
                </AlertDescription>
              </Alert>
            )}

            {/* Quick Toggle */}
            <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  <h3 className="font-semibold leading-none tracking-tight">Bulk Upload Functionality</h3>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={handleQuickToggle}
                  disabled={saving}
                  className="ml-auto"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-1.5">
                Enable or disable the ability to bulk upload data via CSV files
              </p>
            </div>

            {/* Universal Import Section */}
            <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  <h3 className="font-semibold leading-none tracking-tight">Universal Import</h3>
                </div>
                <Button onClick={() => setUniversalDialogOpen(true)} disabled={!enabled}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Data
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Import data across all entity types in a single XLSX file (multi-sheet). Automatically handles dependencies and hierarchies.
              </p>
            </div>

            {/* Info */}
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">About Bulk Upload</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>
                    <strong>CSV Import:</strong> When enabled, admin pages will show a "Bulk Upload" button allowing CSV file imports
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>
                    <strong>Supported Entities:</strong> Users, Locations, Organizations, Sections, Topics, Service Definitions, and various Type entities
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>
                    <strong>Template Downloads:</strong> Each bulk upload dialog provides a CSV template with the correct column headers
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>
                    <strong>Validation:</strong> All uploaded data is validated before import, with detailed error reporting
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>
                    <strong>Limit:</strong> Maximum 500 rows per upload to ensure system performance
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>
                    <strong>Admin Only:</strong> Bulk upload functionality is only available to global administrators
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="px-6 py-6">
            <div className="mb-4">
              <h3 className="font-semibold">Import History</h3>
              <p className="text-sm text-muted-foreground mt-1">
                System-wide log of all bulk imports. Click a row to view per-row details.
              </p>
            </div>
            <ImportHistoryTable />
          </div>
        </TabsContent>
      </Tabs>

      {/* Universal Import Dialog */}
      <UniversalImportDialog
        open={universalDialogOpen}
        onOpenChange={setUniversalDialogOpen}
        onComplete={() => {
          // Optional: refresh history table
        }}
      />
    </div>
  );
};

export default BulkUploadTab;



